import type {
  LaunchWorkflowId,
  WorkflowLaunchInputs,
} from "./generated/workflowLaunch";

export type LaunchStep = {
  id: "source" | "product" | "goal";
  title: string;
  field: keyof WorkflowLaunchInputs;
  integration?: "shopify";
  requires: readonly ("project" | "shopify" | "source" | "product" | "goal")[];
};
export const LAUNCH_WORKFLOWS: Record<
  LaunchWorkflowId,
  {
    id: LaunchWorkflowId;
    version: 1;
    title: string;
    description: string;
    steps: readonly LaunchStep[];
  }
> = {
  "ad-to-lander": {
    id: "ad-to-lander",
    version: 1,
    title: "Turn a winning ad into its landing page",
    description:
      "Build a page that matches your ad’s hook, claims, and imagery.",
    steps: [
      { id: "source", title: "Add your ad", field: "sourceUrl", requires: [] },
      {
        id: "product",
        title: "Choose your Shopify product",
        field: "productId",
        integration: "shopify",
        requires: ["project", "shopify"],
      },
      {
        id: "goal",
        title: "Set conversion goal",
        field: "conversionGoal",
        requires: [],
      },
    ],
  },
  "competitor-teardown": {
    id: "competitor-teardown",
    version: 1,
    title: "Study a competitor’s funnel",
    description:
      "Understand their positioning, offers, and opportunities for your product.",
    steps: [
      {
        id: "source",
        title: "Add a competitor page",
        field: "sourceUrl",
        requires: [],
      },
      {
        id: "product",
        title: "Describe your product",
        field: "productDescription",
        requires: [],
      },
    ],
  },
};
const URL_SCHEME_PATTERN = /^[a-z][a-z\d+.-]*:/i;
const HOST_WITH_PORT_PATTERN =
  /^(?:localhost|(?:[a-z\d-]+\.)+[a-z\d-]+):\d+(?:[/?#]|$)/i;

export function normalizeWorkflowSourceUrl(value: string) {
  const trimmedValue = value.trim();
  if (!trimmedValue || /\s/.test(trimmedValue)) {
    return null;
  }

  const hasExplicitScheme =
    URL_SCHEME_PATTERN.test(trimmedValue) &&
    !HOST_WITH_PORT_PATTERN.test(trimmedValue);
  const valueWithProtocol = hasExplicitScheme
    ? trimmedValue
    : `https://${trimmedValue}`;

  try {
    const url = new URL(valueWithProtocol);
    if (
      (url.protocol !== "http:" && url.protocol !== "https:") ||
      url.username ||
      url.password
    ) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}
export function isWorkflowSourceUrl(value: string) {
  return Boolean(normalizeWorkflowSourceUrl(value));
}
export function resolveLaunchSteps({
  workflowId,
  inputs,
  projectId,
  shopifyConnected,
  productAvailable,
}: {
  workflowId: LaunchWorkflowId;
  inputs: WorkflowLaunchInputs;
  projectId: string | null;
  shopifyConnected: boolean;
  productAvailable: boolean;
}) {
  const workflow = LAUNCH_WORKFLOWS[workflowId];
  const valid = {
    source: isWorkflowSourceUrl(inputs.sourceUrl),
    product:
      workflowId === "ad-to-lander"
        ? Boolean(inputs.productId) && productAvailable
        : Boolean(inputs.productDescription.trim()),
    goal: Boolean(inputs.conversionGoal.trim()),
  };
  const steps = workflow.steps.map((step) => {
    const missing = step.requires.filter((requirement) => {
      if (requirement === "project") {
        return !projectId;
      }
      if (requirement === "shopify") {
        return !shopifyConnected;
      }
      return !valid[requirement];
    });
    let state: "blocked" | "complete" | "editable" = "editable";
    if (missing.length) {
      state = "blocked";
    } else if (valid[step.id]) {
      state = "complete";
    }
    return {
      ...step,
      state,
      missing,
    };
  });
  return { steps, ready: steps.every((step) => step.state === "complete") };
}
export function buildWorkflowPrompt({
  workflowId,
  inputs,
  productTitle,
}: {
  workflowId: LaunchWorkflowId;
  inputs: WorkflowLaunchInputs;
  productTitle?: string;
}) {
  const source =
    normalizeWorkflowSourceUrl(inputs.sourceUrl) ||
    (workflowId === "ad-to-lander" ? "[Add ad URL]" : "[Add competitor URL]");
  if (workflowId === "competitor-teardown") {
    return `Study the funnel at ${source} for ${inputs.productDescription.trim() || "[Describe your product]"}. Analyze its positioning, offers, page structure, and conversion journey. Explain what works, where it falls short, and what we can learn. Do not rebuild or publish anything.`;
  }
  return `Here's a winning ad creative: ${source}. Build a landing page that matches it: same hook, same claims in the same order, same visual language, so the click lands on the promise the ad made.\n\nTranscribe the ad and use its language for the headline and section copy. Pull stills from the video for imagery where they hold up; fill the rest from my brand assets.\n\nThe page sells ${productTitle || "[Choose your Shopify product]"}${inputs.productId ? ` (product ID: ${inputs.productId})` : ""} and the conversion that matters is ${inputs.conversionGoal.trim() || "[Choose conversion goal]"}. Build it as a draft and walk me through what you matched. Don't publish until I approve.`;
}
